'use client';

import React, { useState } from 'react';

import { Inbox } from '@depop/web-thrift-ui/Icons/Inbox';
import { MessageIcon } from '@depop/web-ui-kit/Icons/MessageIcon';
import { Modal } from '@depop/web-ui-kit/Modal';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ChipLink } from '@/components/Chip/ChipLink';
import { DownloadAppModal } from '@/components/DownloadAppModal/DownloadAppModal';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants.ts';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker.ts';
import { buildMessagesCreateUrl } from '@/modules/messages/helpers';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking.ts';
import { ReceiptType } from '@/modules/receipts/types';
import { useIsViewingOwnShop } from '@/modules/shop/hooks/useIsViewingOwnShop';
import { useCurrentUser } from '@/modules/user/useCurrentUser.ts';

type MessageProps = {
  productId?: number;
  userId?: number;
  username?: string;
  type?: ReceiptType;
  isChipStyle?: boolean;
};

export function MessageButton({
  productId,
  userId,
  username,
  type,
  isChipStyle,
}: MessageProps) {
  const t = useTranslations('common');
  const [isAuthenticated, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const isViewingOwnProfile = useIsViewingOwnShop();

  const url = buildMessagesCreateUrl({ recipientId: userId, productId });
  const messages = {
    [ReceiptType.Seller]: t('QuickMessage.MessageBuyer'),
    [ReceiptType.Buyer]: t('QuickMessage.MessageSeller'),
  };

  function handleClick() {
    if (productId) {
      return sendProductInteractionActionATEvent(productId, 'ProductChat');
    }

    return sendActivityTrackerEvent(
      ActivityTrackerEventType.USER_INTERACTION_ACTION,

      {
        interactionType: 'Chat',
        userId,
      }
    );
  }

  function openModal() {
    setIsDownloadModalOpen(true);

    if (productId) {
      sendActivityTrackerEvent(
        ActivityTrackerEventType.PRODUCT_INTERACTION_ACTION,

        {
          interactionType: 'ProductChat',
          productId,
          schemaVersion: '4.0',
        }
      );
    }
  }

  if (isViewingOwnProfile) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <>
        <button
          className={styles.messageButton}
          onClick={openModal}
          aria-haspopup="dialog"
          aria-label={t('NewMessage')}
        >
          <MessageIcon
            type="message"
            title={type ? messages[type] : t('MessageUserDefault')}
          />
        </button>
        <Modal
          isOpen={isDownloadModalOpen}
          setOpen={setIsDownloadModalOpen}
          content={() => (
            <DownloadAppModal downloadAppTrackingId="messages_button" />
          )}
        />
      </>
    );
  }

  if (isChipStyle) {
    return (
      <ChipLink
        href={url}
        onClick={handleClick}
        aria-label={
          username ? t('MessageUser', { username }) : t('MessageUserDefault')
        }
      >
        <Inbox size="sm" />
        {type ? messages[type] : t('MessageUserDefault')}
      </ChipLink>
    );
  }

  return (
    <ExtendedLink
      className={styles.messageButton}
      href={url}
      onClick={handleClick}
      aria-label={
        username ? t('MessageUser', { username }) : t('MessageUserDefault')
      }
    >
      <MessageIcon
        type="message"
        title={type ? messages[type] : t('MessageUserDefault')}
      />
    </ExtendedLink>
  );
}
